<script setup lang="ts">
   import { ref, computed, onMounted, onUnmounted } from 'vue';
   import { useRoute } from '#imports';
   import { useScrollNav } from '~/composables/useScrollNav';
   import { useWowPoloNavigation } from '../composables/useWowPoloNavigation';
   import type {
      WowPoloMenuChild,
      WowPoloMenuItem,
   } from '../composables/useWowPoloNavigation';
   import { usePublicGalleries } from '~/composables/usePublicGalleries';

   const route = useRoute();
   const { isVisible, isScrolled } = useScrollNav();
   const { visibleItems } = useWowPoloNavigation();

   // Fetch galleries for the dropdown
   const config = useRuntimeConfig();
   const portal = config.public.portalBrand as string;
   const { galleriesData } = usePublicGalleries(portal);
   const galleryChildren = computed(() => {
      return (galleriesData.value?.data ?? []).map((g) => ({
         label: g.title,
         to: `/gallery/${g.slug}`,
      }));
   });

   const showMobileNav = ref(false);
   const expandedMobileItem = ref<string | null>(null);
   const hoveredDesktopItem = ref<string | null>(null);
   let scrollCloseTimeout: ReturnType<typeof setTimeout> | null = null;

   function toggleMobileNav() {
      showMobileNav.value = !showMobileNav.value;
      expandedMobileItem.value = null;
   }

   function toggleMobileSubmenu(label: string) {
      expandedMobileItem.value = expandedMobileItem.value === label ? null : label;
   }

   function closeMobileNavOnScroll() {
      if (window.innerWidth >= 1024) {
         return;
      }
      if (!showMobileNav.value) {
         return;
      }
      if (scrollCloseTimeout) {
         clearTimeout(scrollCloseTimeout);
      }
      scrollCloseTimeout = setTimeout(() => {
         showMobileNav.value = false;
      }, 270);
   }

   function isActive(path: string): boolean {
      if (path === '/') {
         return route.path === '/';
      }
      return route.fullPath === path;
   }

   function isActiveOrChild(item: WowPoloMenuItem): boolean {
      const to = typeof item.to === 'string' ? item.to : null;

      if (to && isActive(to)) {
         return true;
      }

      if (!item.children || !to || to === '/') {
         return false;
      }

      // A dropdown parent stays highlighted while you are on one of its pages, e.g.
      // /gallery/some-album under /gallery. Items with no `to` of their own - the
      // waivers menu, whose children are off-site - are never highlighted.
      return route.path.startsWith(to);
   }

   // Shared by both trigger branches so a link-backed item and a dropdown-only item
   // cannot drift apart visually. The reset utilities matter only for the <button>
   // branch, where user-agent styling would otherwise add a border and background.
   const NAV_TRIGGER_BASE =
      'relative flex items-center gap-1 px-5 py-6 text-sm font-semibold uppercase tracking-wide transition-colors duration-200';
   const NAV_TRIGGER_BUTTON_RESET =
      'm-0 cursor-pointer appearance-none border-0 bg-transparent';

   const MOBILE_TRIGGER_BASE =
      'flex-1 px-6 py-4 text-left text-sm font-semibold uppercase tracking-wide transition-colors duration-200';

   function getChildren(item: WowPoloMenuItem): WowPoloMenuChild[] {
      if (item.label !== 'Gallery') {
         return item.children ?? [];
      }
      return galleryChildren.value;
   }

   onMounted(() => {
      window.addEventListener('scroll', closeMobileNavOnScroll, { passive: true });
   });

   onUnmounted(() => {
      window.removeEventListener('scroll', closeMobileNavOnScroll);
      if (scrollCloseTimeout) {
         clearTimeout(scrollCloseTimeout);
      }
   });
</script>

<template>
   <!-- Desktop Nav -->
   <nav
      data-nav-main
      class="fixed top-0 z-50 hidden w-full bg-white transition-all duration-300 lg:block"
      :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'"
      style="box-shadow: 1px 1px 16px 0 rgba(0, 0, 0, 0.24)">
      <div class="mx-auto flex max-w-7xl items-center justify-between px-6">
         <!-- Logo -->
         <NuxtLink to="/" class="shrink-0 py-2">
            <img
               src="/img/wow-polo-logo.webp"
               alt="WOW Polo Series"
               class="rounded-full transition-all duration-300"
               :class="isScrolled ? 'h-10' : 'h-24'" />
         </NuxtLink>

         <!-- Nav items -->
         <ul class="flex items-center gap-1">
            <li
               v-for="item in visibleItems"
               :key="item.label"
               class="relative"
               @mouseenter="hoveredDesktopItem = item.label"
               @mouseleave="hoveredDesktopItem = null">

               <!-- Literal tags rather than <component :is>. A dynamic string resolves
                    against the component registry first, and @primevue/nuxt-module
                    registers a global Button, so :is="'button'" rendered a themed
                    PrimeVue button instead of a plain one. -->
               <NuxtLink
                  v-if="item.to"
                  :to="item.to"
                  :aria-expanded="item.children !== undefined ? hoveredDesktopItem === item.label : undefined"
                  :class="[
                     NAV_TRIGGER_BASE,
                     isActiveOrChild(item) ? 'wow-nav-active' : 'wow-nav-inactive',
                  ]">
                  {{ item.label }}
                  <svg
                     v-if="item.children !== undefined"
                     class="size-3.5 transition-transform duration-200"
                     :class="hoveredDesktopItem === item.label ? 'rotate-180' : ''"
                     fill="none"
                     stroke="currentColor"
                     viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                  <span
                     v-if="isActiveOrChild(item)"
                     class="wow-nav-underline absolute inset-x-5 bottom-3 h-0.5"></span>
               </NuxtLink>

               <!-- Dropdown-only item: no page of its own, so never active and no
                    underline. -->
               <button
                  v-else
                  type="button"
                  :aria-expanded="hoveredDesktopItem === item.label"
                  :class="[NAV_TRIGGER_BASE, 'wow-nav-inactive', NAV_TRIGGER_BUTTON_RESET]"
                  @click="hoveredDesktopItem = hoveredDesktopItem === item.label ? null : item.label">
                  {{ item.label }}
                  <svg
                     v-if="item.children !== undefined"
                     class="size-3.5 transition-transform duration-200"
                     :class="hoveredDesktopItem === item.label ? 'rotate-180' : ''"
                     fill="none"
                     stroke="currentColor"
                     viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
               </button>

               <!-- Desktop dropdown -->
               <div
                  v-if="item.children !== undefined && hoveredDesktopItem === item.label && getChildren(item).length > 0"
                  class="absolute left-0 top-full min-w-[280px] rounded-b-lg bg-white py-2 shadow-lg">
                  <template v-for="child in getChildren(item)" :key="child.to">
                     <a
                        v-if="child.external"
                        :href="child.to"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block px-5 py-2.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-[#5cc8d9]"
                        @click="hoveredDesktopItem = null">
                        {{ child.label }}
                     </a>
                     <NuxtLink
                        v-else
                        :to="child.to"
                        class="block px-5 py-2.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-[#5cc8d9]"
                        @click="hoveredDesktopItem = null">
                        {{ child.label }}
                     </NuxtLink>
                  </template>
               </div>
            </li>
         </ul>
      </div>
   </nav>

   <!-- Mobile Nav -->
   <div class="lg:hidden">
      <!-- Brand bar -->
      <div
         class="wow-brand-bar fixed top-0 z-50 flex h-16 w-full items-center px-4 transition-all duration-300"
         :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'">
         <!-- Logo -->
         <NuxtLink to="/" class="shrink-0">
            <img
               src="/img/wow-polo-logo.webp"
               alt="WOW Polo Series"
               class="h-10 rounded-full" />
         </NuxtLink>

         <!-- Brand name -->
         <span class="flex-1 text-center text-sm font-bold uppercase tracking-wider text-white">
            WOW Polo Series
         </span>

         <!-- Hamburger -->
         <button
            @click="toggleMobileNav"
            class="flex items-center justify-center p-2 text-white"
            aria-label="Toggle navigation menu">
            <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  v-if="!showMobileNav"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16" />
               <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12" />
            </svg>
         </button>
      </div>

      <!-- Mobile drawer -->
      <div
         class="fixed left-0 top-16 z-40 w-full bg-white shadow-lg transition-all duration-300"
         :class="showMobileNav ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 pointer-events-none'">
         <ul class="flex flex-col">
            <li
               v-for="item in visibleItems"
               :key="item.label"
               class="border-b border-gray-100">

               <!-- Items without children -->
               <NuxtLink
                  v-if="item.children === undefined"
                  :to="item.to"
                  @click="showMobileNav = false"
                  class="flex items-center px-6 py-4 text-sm font-semibold uppercase tracking-wide transition-colors duration-200"
                  :class="isActiveOrChild(item)
                     ? 'wow-nav-active'
                     : 'wow-nav-inactive'">
                  {{ item.label }}
               </NuxtLink>

               <!-- Items with children -->
               <template v-else>
                  <div class="flex items-center">
                     <!-- With no page of its own, the label is part of the toggle rather
                          than a separate link, so the whole row opens the submenu. -->
                     <NuxtLink
                        v-if="item.to"
                        :to="item.to"
                        @click="showMobileNav = false"
                        :class="[
                           MOBILE_TRIGGER_BASE,
                           isActiveOrChild(item) ? 'wow-nav-active' : 'wow-nav-inactive',
                        ]">
                        {{ item.label }}
                     </NuxtLink>
                     <button
                        v-else
                        type="button"
                        :aria-expanded="expandedMobileItem === item.label"
                        :class="[
                           MOBILE_TRIGGER_BASE,
                           'wow-nav-inactive',
                           NAV_TRIGGER_BUTTON_RESET,
                        ]"
                        @click="toggleMobileSubmenu(item.label)">
                        {{ item.label }}
                     </button>
                     <button
                        v-if="getChildren(item).length > 0"
                        type="button"
                        class="p-4 text-gray-400"
                        aria-label="Expand submenu"
                        @click="toggleMobileSubmenu(item.label)">
                        <svg
                           class="size-4 transition-transform duration-200"
                           :class="expandedMobileItem === item.label ? 'rotate-180' : ''"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                     </button>
                  </div>

                  <!-- Mobile sub-items -->
                  <ul
                     v-if="expandedMobileItem === item.label && getChildren(item).length > 0"
                     class="bg-gray-50">
                     <li
                        v-for="child in getChildren(item)"
                        :key="child.to">
                        <a
                           v-if="child.external"
                           :href="child.to"
                           target="_blank"
                           rel="noopener noreferrer"
                           @click="showMobileNav = false"
                           class="block px-10 py-3 text-sm text-gray-600 transition-colors hover:text-[#5cc8d9]">
                           {{ child.label }}
                        </a>
                        <NuxtLink
                           v-else
                           :to="child.to"
                           @click="showMobileNav = false"
                           class="block px-10 py-3 text-sm text-gray-600 transition-colors hover:text-[#5cc8d9]">
                           {{ child.label }}
                        </NuxtLink>
                     </li>
                  </ul>
               </template>
            </li>
         </ul>
      </div>
   </div>
</template>

<style scoped>
   .wow-nav-active {
      color: #5cc8d9;
   }

   .wow-nav-inactive {
      color: #374151;
   }

   .wow-nav-inactive:hover {
      color: #5cc8d9;
   }

   .wow-nav-underline {
      background-color: #7cdfef;
   }

   .wow-brand-bar {
      background-color: #1a1a2e;
   }
</style>
